import { useContext } from 'react';

import { DialogProps } from '@cast/design-system';
import { K8sProvider } from '@cast/shared-types';
import { Cluster } from '@cast/types';

import { FeatureSelection, FeatureName } from 'hooks/dialogs/cluster/types';

import { SlideDialog } from '../_components/SlideDialog';
import { ConfigureCastAnywhereStep } from './_components/steps/ConfigureCastAnywhereStep';
import { ConnectingStep } from './_components/steps/ConnectingStep';
import { SelectFeaturesStep } from './_components/steps/SelectFeaturesStep';
import { SelectProviderStep } from './_components/steps/SelectProviderStep';
import { SuccessStep } from './_components/steps/SuccessStep';
import {
  OnboardClusterContext,
  OnboardClusterProvider,
} from './OnboardClusterProvider';
import { ErrorStep } from '../_components/new-onboarding';

type DialogRootProps = Omit<DialogProps, 'onClose'> & {
  onClose: () => void;
  onCompleted: () => void;
  onSwitchToCastctl: () => void;
};

const DialogRoot = ({
  onClose,
  onCompleted,
  onSwitchToCastctl,
  ...props
}: DialogRootProps) => {
  const {
    state,
    goBackFromConnecting,
    handleResetState,
    handleWaitForConnection,
  } = useContext(OnboardClusterContext);

  return (
    <SlideDialog
      testId="phase-1-cluster-onboarding-dialog"
      wideSlides={['selectFeatures']}
      onClose={onClose}
      activeSlide={state}
      slides={{
        selectProvider: (
          <SelectProviderStep
            onClose={onClose}
            onSwitchToCastctl={onSwitchToCastctl}
          />
        ),
        selectFeatures: (
          <SelectFeaturesStep
            onClose={onClose}
            onRunTheScript={() => {
              handleWaitForConnection();
            }}
            onBack={() => {
              handleResetState();
            }}
          />
        ),
        connecting: (
          <ConnectingStep onClose={onClose} onBack={goBackFromConnecting} />
        ),
        error: (
          <ErrorStep
            title="Failed to connect cluster"
            testId="connecting-failed-step"
            onClose={onClose}
            onRetry={handleResetState}
          />
        ),
        configureCastAnywhere: <ConfigureCastAnywhereStep onClose={onClose} />,
        success: <SuccessStep onDone={onCompleted} onClose={onCompleted} />,
      }}
      {...props}
    />
  );
};

type OnboardClusterDialogProps = DialogRootProps & {
  initialProvider: K8sProvider;
  initialAnywhereClusterName?: string;
  promotedFeature?: FeatureName;
  onClusterConnected: (cluster: Cluster) => void;
  onFeaturesSelected: (features: FeatureSelection) => void;
  onCompleted: () => void;
  onSwitchToCastctl: () => void;
};

export const LegacyOnboardClusterDialog = ({
  initialProvider,
  initialAnywhereClusterName,
  promotedFeature,
  onClusterConnected,
  onCompleted,
  onFeaturesSelected,
  onSwitchToCastctl,
  ...props
}: OnboardClusterDialogProps) => {
  return (
    <OnboardClusterProvider
      initialProvider={initialProvider}
      initialAnywhereClusterName={initialAnywhereClusterName}
      promotedFeature={promotedFeature}
      onCompleted={onCompleted}
      onClusterConnected={onClusterConnected}
      onFeaturesSelected={onFeaturesSelected}
    >
      <DialogRoot
        {...props}
        onCompleted={onCompleted}
        onSwitchToCastctl={onSwitchToCastctl}
      />
    </OnboardClusterProvider>
  );
};
